export const MAX_FAILED_ATTEMPTS = 5;
export const LOCKOUT_DURATION_MS = 20 * 60 * 1000;

const STORAGE_PREFIX = "login-lockout";

const buildKey = (scope, identifier) => {
  if (!scope || !identifier) return null;
  return `${STORAGE_PREFIX}:${scope}:${identifier}`;
};

const readRecord = (key) => {
  if (!key) return { failures: 0, lockUntil: 0 };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { failures: 0, lockUntil: 0 };
    const parsed = JSON.parse(raw);
    return {
      failures: Number(parsed.failures) || 0,
      lockUntil: Number(parsed.lockUntil) || 0,
    };
  } catch {
    return { failures: 0, lockUntil: 0 };
  }
};

const writeRecord = (key, record) => {
  if (!key) return;
  localStorage.setItem(
    key,
    JSON.stringify({
      failures: Number(record.failures) || 0,
      lockUntil: Number(record.lockUntil) || 0,
    })
  );
};

const clearRecord = (key) => {
  if (!key) return;
  localStorage.removeItem(key);
};

export const getLockoutStatus = (scope, identifier) => {
  const key = buildKey(scope, identifier);
  if (!key) {
    return {
      attemptsLeft: MAX_FAILED_ATTEMPTS,
      isLocked: false,
      remainingMs: 0,
      lockoutUntil: 0,
    };
  }

  const now = Date.now();
  const record = readRecord(key);

  if (record.lockUntil && now >= record.lockUntil) {
    clearRecord(key);
    return {
      attemptsLeft: MAX_FAILED_ATTEMPTS,
      isLocked: false,
      remainingMs: 0,
      lockoutUntil: 0,
    };
  }

  if (record.lockUntil && now < record.lockUntil) {
    return {
      attemptsLeft: 0,
      isLocked: true,
      remainingMs: record.lockUntil - now,
      lockoutUntil: record.lockUntil,
    };
  }

  const attemptsLeft = Math.max(
    0,
    MAX_FAILED_ATTEMPTS - (record.failures || 0)
  );

  return {
    attemptsLeft,
    isLocked: false,
    remainingMs: 0,
    lockoutUntil: 0,
  };
};

export const registerFailure = (scope, identifier) => {
  const key = buildKey(scope, identifier);
  if (!key) {
    return getLockoutStatus(scope, identifier);
  }

  const record = readRecord(key);
  const failures = (record.failures || 0) + 1;

  if (failures >= MAX_FAILED_ATTEMPTS) {
    const lockUntil = Date.now() + LOCKOUT_DURATION_MS;
    writeRecord(key, { failures, lockUntil });
  } else {
    writeRecord(key, { failures, lockUntil: 0 });
  }

  return getLockoutStatus(scope, identifier);
};

export const clearLockout = (scope, identifier) => {
  const key = buildKey(scope, identifier);
  clearRecord(key);
};

export const formatRemainingMinutes = (remainingMs) => {
  if (!remainingMs || remainingMs <= 0) return "0 minutes";
  const minutes = Math.ceil(remainingMs / 60000);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
};